import { useEffect, useMemo, useState } from 'react';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import { alpha, useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';

import useLocalization from 'hooks/useLocalization';
import { useHeaderSettingsAlert } from 'hooks/useHeaderSettingsAlert';
import { getNestedValue } from 'utils/helpers';

type HeaderSettingsStatusIconProps = {
  /** סגירת טולטיפ כשתפריט הפרופיל פתוח — מונע חפיפה עם ה-Popper */
  suppressTooltip?: boolean;
};

function resolveLocaleText(
  currentLocale: object,
  path: string,
  values?: Record<string, string | number>,
  fallback = '',
): string {
  const value = getNestedValue(currentLocale, path);
  const template = typeof value === 'string' && value.trim() ? value : fallback;
  if (!values) return template;

  return Object.entries(values).reduce(
    (result, [key, val]) => result.replaceAll(`{${key}}`, String(val)),
    template,
  );
}

// ==============================|| HEADER SETTINGS STATUS ||============================== //

export default function HeaderSettingsStatusIcon({ suppressTooltip = false }: HeaderSettingsStatusIconProps) {
  const theme = useTheme();
  const { currentLocale } = useLocalization();
  const settingsAlert = useHeaderSettingsAlert();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const loc = currentLocale.header.settingsAlert;
  const fixTargets = loc.fixTargets as Record<string, string>;

  const hasIssues = settingsAlert.showBadge;
  const issueCount = settingsAlert.issues.length;
  const hasErrorIssues = settingsAlert.issues.some((issue) => issue.severity === 'error');

  const ariaLabel = useMemo(() => {
    if (!hasIssues) return loc.allOk;
    if (issueCount === 1) return loc.ariaNeedsAttention;
    return loc.ariaWithCount.replace('{count}', String(issueCount));
  }, [hasIssues, issueCount, loc]);

  const tooltipTitle = hasIssues ? (
    <Stack spacing={1.25} sx={{ textAlign: 'start', py: 0.25 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.35 }}>
        {loc.title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.45 }}>
        {loc.intro}
      </Typography>
      <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {settingsAlert.issues.map((issue) => {
          const summary = resolveLocaleText(currentLocale, issue.messagePath, issue.messageValues);
          const fixLabel = fixTargets[issue.fixTargetKey] ?? loc.fixLinkShort;

          return (
            <Box component="li" key={issue.id}>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.45 }}>
                {summary}
              </Typography>
              <Link
                component={RouterLink}
                to={issue.fixPath}
                underline="hover"
                variant="caption"
                sx={{
                  fontWeight: 600,
                  display: 'inline-block',
                  mt: 0.25,
                  color: 'primary.main',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {fixLabel} ←
              </Link>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  ) : (
    loc.allOk
  );

  useEffect(() => {
    if (suppressTooltip) {
      setTooltipOpen(false);
    }
  }, [suppressTooltip]);

  return (
    <Tooltip
      title={tooltipTitle}
      open={tooltipOpen && !suppressTooltip}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
      enterDelay={hasIssues ? 250 : 400}
      enterNextDelay={200}
      leaveDelay={120}
      enterTouchDelay={hasIssues ? 500 : 700}
      placement="bottom"
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
        },
        tooltip: {
          sx: {
            pointerEvents: 'auto',
            maxWidth: 320,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.shadows[6],
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
          },
        },
      }}
    >
      <IconButton
        size="small"
        color="inherit"
        aria-label={ariaLabel}
        sx={{
          p: 0.35,
          color: hasIssues ? (hasErrorIssues ? 'error.main' : 'warning.dark') : 'inherit',
          '&:hover': {
            bgcolor: hasIssues
              ? alpha(hasErrorIssues ? theme.palette.error.main : theme.palette.warning.main, 0.12)
              : alpha(theme.palette.action.active, 0.06),
          },
        }}
      >
        <Badge
          badgeContent={hasIssues ? issueCount : 0}
          color={hasErrorIssues ? 'error' : 'warning'}
          overlap="circular"
          invisible={!hasIssues}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              minWidth: 16,
              height: 16,
              fontWeight: 700,
            },
          }}
        >
          <SettingsTwoToneIcon sx={{ fontSize: 24, display: 'block' }} aria-hidden />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
